import { useState } from 'react';
import { Check, Copy, KeyRound } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { FormSection } from '../../components/tracking-details-form/base';

// Displays a freshly generated signing secret exactly once. The secret is never
// returned again on reads, so the merchant must copy it now. Use it to verify
// the X-Contextual-Signature header (HMAC-SHA256 over "<timestamp>.<body>").
//
// The optional onAcknowledge prop renders a "Continue" button that the caller
// uses to advance any wizard step the user was in when the secret was issued —
// keeps the callout visible until the merchant explicitly confirms they have
// the secret, instead of auto-navigating away on save.
export const WebhookSigningSecretCallout = ({
  secret,
  onAcknowledge
}: {
  secret: string;
  onAcknowledge?: () => void;
}) => {
  const [copied, setCopied] = useState(false);

  const onCopy = () => {
    void navigator.clipboard.writeText(secret).then(() => {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    });
  };

  return (
    <FormSection>
      <Alert variant="warning">
        <KeyRound className="h-4 w-4" />
        <AlertTitle>Copy your signing secret now</AlertTitle>
        <AlertDescription>
          <p className="mb-2">
            This is the only time we will show this secret. Store it securely — use it to verify the{' '}
            <code>X-Contextual-Signature</code> header on incoming requests. Saving again generates
            a new secret.
          </p>
          <div className="flex items-center gap-2">
            <code className="block w-full overflow-x-auto rounded bg-background px-2 py-1 text-sm">
              {secret}
            </code>
            <Button type="button" variant="outline" size="sm" onClick={onCopy}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              <span className="ml-1">{copied ? 'Copied' : 'Copy'}</span>
            </Button>
          </div>
          {onAcknowledge && (
            <div className="mt-3 flex justify-end">
              <Button type="button" size="sm" onClick={onAcknowledge}>
                I&apos;ve copied my secret, continue
              </Button>
            </div>
          )}
        </AlertDescription>
      </Alert>
    </FormSection>
  );
};
